/**
 * « À traiter » (porteur, 12/10/2026) : les documents qui attendent
 * l'utilisateur connecte AVANT leur envoi en signature.
 *
 *  - Remplisseur : le document, avec ses champs a leur place ; il saisit,
 *    puis « J'ai terminé ».
 *  - Valideur : le document et tout ce qui a ete rempli ; « Valider et
 *    lancer la signature » ou « Refuser » (motif obligatoire : il part a
 *    l'emetteur et aux remplisseurs).
 *
 * Rien de ce qui se passe ici n'entre dans le dossier de preuve : la
 * signature commence apres.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, type ChampATraiter, type DemandeSignataire, type TacheATraiter, type Traitement } from "../api";
import { DemandesSignataires } from "../components/DemandesSignataires";
import { PdfViewer } from "../components/PdfViewer";
import { Chargement, Erreur, Icone, TRACES, Vide, date, telecharger } from "../components/ui";

const ROLES = { remplit: "À compléter", valide: "À valider avant envoi" } as const;

/** Delai d'enregistrement d'une saisie au clavier (texte, date). */
const DELAI_MS = 600;

const coche = (c: ChampATraiter) => c.kind === "case" || c.kind === "radio";
/** La date saisie (AAAA-MM-JJ) lue a la francaise : JJ/MM/AAAA. */
const lisible = (c: ChampATraiter) => (c.kind === "date_saisie" && c.valeur && /^\d{4}-\d{2}-\d{2}$/.test(c.valeur)
  ? c.valeur.split("-").reverse().join("/") : c.valeur);
const idChamp = (c: ChampATraiter) => `champ-${c.id}`;

function Saisie({ champ, invalide, onChange }: {
  champ: ChampATraiter; invalide: boolean; onChange: (v: string | null, immediat: boolean) => void;
}) {
  const libelle = champ.libelle || "Champ";
  if (champ.kind === "texte_fixe") return <span className="cadre__texte">{champ.libelle}</span>;
  if (coche(champ)) {
    return (
      <label className="saisie-cadre saisie-cadre--case" title={libelle}>
        <input id={idChamp(champ)} type={champ.kind === "case" ? "checkbox" : "radio"} name={champ.groupe ?? champ.id}
               checked={Boolean(champ.valeur)} aria-label={libelle} aria-invalid={invalide || undefined}
               onChange={(e) => onChange(e.target.checked ? "oui" : null, true)} />
      </label>
    );
  }
  if (champ.kind === "liste") {
    return (
      <select id={idChamp(champ)} className="saisie-cadre" aria-label={libelle} value={champ.valeur ?? ""}
              aria-invalid={invalide || undefined} onChange={(e) => onChange(e.target.value || null, true)}>
        <option value="">Choisir…</option>
        {(champ.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  // Enregistre a la frappe (apres une courte pause), plus a la sortie du
  // champ : le clic sur « J'ai terminé » servait d'abord au blur et il
  // fallait cliquer deux fois (critique impeccable du 13/10/2026).
  return (
    <input id={idChamp(champ)} className="saisie-cadre" aria-label={libelle}
           type={champ.kind === "date_saisie" ? "date" : "text"} value={champ.valeur ?? ""}
           aria-invalid={invalide || undefined} onChange={(e) => onChange(e.target.value || null, false)} />
  );
}

type Etat = "repos" | "enregistrement" | "enregistre";

function Document({ tache, onFini }: { tache: TacheATraiter; onFini: () => void }) {
  const [t, setT] = useState<Traitement | null>(null);
  const [pdf, setPdf] = useState<{ data: ArrayBuffer; blob: Blob } | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [etat, setEtat] = useState<Etat>("repos");
  /** Les champs obligatoires restes vides au clic sur « J'ai terminé ». */
  const [signales, setSignales] = useState<ChampATraiter[]>([]);
  /** Saisies au clavier pas encore envoyees, par champ : parties avant « terminé ». */
  const attente = useRef(new Map<string, { minuteur: number; envoyer: () => Promise<void> }>());

  useEffect(() => {
    void (async () => {
      try {
        setT(await api.traitement(tache.intervenant_id));
        const blob = await api.documentATraiter(tache.intervenant_id);
        setPdf({ data: await blob.arrayBuffer(), blob });
      } catch (e) {
        setErreur(e);
      }
    })();
  }, [tache.intervenant_id]);

  const enregistrer = async (champ: ChampATraiter, valeur: string | null) => {
    attente.current.delete(champ.id);
    setEtat("enregistrement");
    try {
      await api.saisirATraiter(tache.intervenant_id, champ.id, valeur?.trim() || null);
      if (attente.current.size === 0) setEtat("enregistre");
    } catch (e) {
      setErreur(e);
      setEtat("repos");
    }
  };

  /** L'ecran suit la saisie aussitot ; le serveur la recoit tout de suite
   *  (case, choix, liste) ou apres une pause de frappe (texte, date). */
  const saisir = (champ: ChampATraiter, valeur: string | null, immediat: boolean) => {
    setErreur(null);
    setT((x) => x && {
      ...x,
      champs: x.champs.map((c) => (c.id === champ.id ? { ...c, valeur }
        : champ.kind === "radio" && c.kind === "radio" && c.groupe === champ.groupe && valeur ? { ...c, valeur: null } : c)),
    });
    setSignales((v) => v.filter((c) => c.id !== champ.id));
    window.clearTimeout(attente.current.get(champ.id)?.minuteur);
    const envoyer = () => enregistrer(champ, valeur);
    if (immediat) {
      attente.current.delete(champ.id);
      void envoyer();
      return;
    }
    setEtat("enregistrement");
    attente.current.set(champ.id, { minuteur: window.setTimeout(() => void envoyer(), DELAI_MS), envoyer });
  };

  const viderAttente = async () => {
    const restes = [...attente.current.values()];
    restes.forEach((r) => window.clearTimeout(r.minuteur));
    await Promise.all(restes.map((r) => r.envoyer()));
  };

  const agir = async (action: () => Promise<unknown>) => {
    setEnvoi(true);
    setErreur(null);
    try {
      await action();
      onFini();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  const allerA = (c: ChampATraiter) => {
    const el = document.getElementById(idChamp(c));
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    el?.focus({ preventScroll: true });
  };

  if (!t) return erreur ? <Erreur erreur={erreur} /> : <Chargement />;
  const remplisseur = t.role === "remplit";
  const manquants = t.champs.filter((c) => remplisseur && c.obligatoire && c.kind !== "texte_fixe" && c.kind !== "radio"
    && !c.valeur?.trim());

  // « J'ai terminé » reste actif : s'il manque un champ, on y mene et on dit
  // lesquels, plutot qu'un bouton grise muet.
  const terminer = async () => {
    const premier = manquants[0];
    if (premier) {
      setSignales(manquants);
      allerA(premier);
      return;
    }
    await agir(async () => {
      await viderAttente();
      await api.terminerATraiter(t.intervenant_id);
    });
  };

  return (
    <div className="prepa prepa--plein">
      <div className="prepa__document">
        {pdf ? (
          <PdfViewer
            data={pdf.data}
            onTelecharger={() => telecharger(pdf.blob, `${t.title}.pdf`)}
            calque={({ page }) => (
              <div className="calque-cadres" style={{ cursor: "default" }}>
                {t.champs.filter((c) => c.page_no === page).map((c) => {
                  const invalide = signales.some((x) => x.id === c.id);
                  return (
                    <div key={c.id} className={invalide ? "cadre cadre--saisie cadre--manquant" : "cadre cadre--saisie"}
                         style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, width: `${c.w * 100}%`, height: `${c.h * 100}%` }}>
                      {c.kind !== "texte_fixe" && (
                        <span className="cadre__etiquette" aria-hidden="true">
                          {c.libelle || "Champ"}{remplisseur && c.obligatoire && c.kind !== "radio" ? " *" : ""}
                        </span>
                      )}
                      {remplisseur
                        ? <Saisie champ={c} invalide={invalide} onChange={(v, immediat) => saisir(c, v, immediat)} />
                        : (
                          <span className="cadre__texte">
                            {coche(c) ? (c.valeur ? <Icone d={TRACES.coche} taille={14} /> : null) : lisible(c)}
                          </span>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          />
        ) : <Chargement libelle="Chargement du document…" />}
      </div>
      <aside className="prepa__panneau">
        <section className="panneau">
          <h2 className="panneau__titre">{t.title}</h2>
          <p className="muted panneau__aide">
            Envoyé par {t.client}. {remplisseur
              ? "Complétez les champs encadrés sur le document (* : obligatoire). Chaque saisie est enregistrée au fur et à mesure ; la signature partira après votre passage."
              : "Vérifiez ce qui a été complété. En validant, le document part aussitôt en signature."}
          </p>
        </section>
        {!remplisseur && (
          <section className="panneau">
            <h2 className="panneau__titre">Complété</h2>
            {t.champs.length === 0 ? <p className="muted">Aucun champ à compléter.</p> : (
              <ul className="complete">
                {t.champs.filter((c) => c.kind !== "texte_fixe").map((c) => (
                  <li key={c.id}>
                    <span className="complete__libelle">{c.libelle || "Champ"}</span>
                    <span className="complete__valeur">
                      {c.valeur ? (coche(c) ? "Coché" : lisible(c)) : <span className="muted">vide</span>}
                    </span>
                    <span className="muted complete__auteur">{c.rempli_par_nom || c.rempli_par}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        <section className="panneau panneau--envoi">
          <Erreur erreur={erreur} />
          {remplisseur ? (
            <>
              <button type="button" className="btn btn--primary btn--large" disabled={envoi}
                      onClick={() => void terminer()}>
                J'ai terminé
              </button>
              <p className="muted panneau__aide" aria-live="polite">
                {etat === "enregistrement" ? "Enregistrement…" : etat === "enregistre" ? "Enregistré." : ""}
                {manquants.length > 0 && signales.length === 0
                  && ` ${manquants.length} champ${manquants.length > 1 ? "s" : ""} obligatoire${manquants.length > 1 ? "s" : ""} à compléter.`}
              </p>
              {signales.length > 0 && (
                <div className="alert alert--warning" role="alert">
                  <p style={{ margin: 0 }}>Avant de terminer, complétez :</p>
                  <ul className="manquants">
                    {signales.map((c) => (
                      <li key={c.id}>
                        <button type="button" className="btn btn--link btn--inline" onClick={() => allerA(c)}>
                          {c.libelle || "Champ"} (page {c.page_no})
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : refus === null ? (
            <>
              <button type="button" className="btn btn--primary btn--large" disabled={envoi}
                      onClick={() => void agir(() => api.validerATraiter(t.intervenant_id, true))}>
                Valider et lancer la signature
              </button>
              <button type="button" className="btn btn--secondary" disabled={envoi} onClick={() => setRefus("")}>
                Refuser
              </button>
            </>
          ) : (
            <>
              <label className="champ-court">
                <span>Motif du refus</span>
                <textarea className="input" rows={3} maxLength={2000} value={refus} autoFocus
                          onChange={(e) => setRefus(e.target.value)} />
              </label>
              <p className="muted panneau__aide">
                L'émetteur et les personnes qui ont complété le document reçoivent ce motif. Rien ne part en signature.
              </p>
              <button type="button" className="btn btn--danger" disabled={envoi || !refus.trim()}
                      onClick={() => void agir(() => api.validerATraiter(t.intervenant_id, false, refus))}>
                Confirmer le refus
              </button>
              <button type="button" className="btn btn--link" onClick={() => setRefus(null)}>Annuler</button>
            </>
          )}
          <button type="button" className="btn btn--link" onClick={onFini}>← Retour à la liste</button>
        </section>
      </aside>
    </div>
  );
}

export function ATraiter() {
  const [taches, setTaches] = useState<TacheATraiter[] | null>(null);
  const [ouverte, setOuverte] = useState<TacheATraiter | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  // Lot 2bis : demandes des signataires du compte (transmission, nom).
  const [demandes, setDemandes] = useState<DemandeSignataire[]>([]);

  const relire = useCallback(async () => {
    void api.demandesEnAttente().then((r) => setDemandes(r.demandes), () => setDemandes([]));
    try {
      setTaches((await api.aTraiter()).a_traiter);
    } catch (e) {
      setErreur(e);
    }
  }, []);
  useEffect(() => void relire(), [relire]);

  if (ouverte) {
    return <Document tache={ouverte} onFini={() => { setOuverte(null); void relire(); }} />;
  }
  return (
    <>
      <h1 className="page-title">À traiter</h1>
      <Erreur erreur={erreur} />
      {demandes.length > 0 && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Demandes des signataires</h2>
          <p className="card__lead">La signature attend votre réponse.</p>
          <DemandesSignataires demandes={demandes} avecTitre onDecide={() => void relire()} />
        </div>
      )}
      {taches === null ? <Chargement /> : taches.length === 0 ? (
        demandes.length > 0 ? null : <Vide>Aucun document ne vous attend avant signature.</Vide>
      ) : (
        <div className="card table-wrap">
          <table className="table--liste">
            <thead>
              <tr><th>Document</th><th>Envoyé par</th><th>À faire</th><th>Depuis</th><th /></tr>
            </thead>
            <tbody>
              {taches.map((t) => (
                <tr key={t.intervenant_id}>
                  <td>{t.title}</td>
                  <td>{t.client}</td>
                  <td><span className="pill pill--pending">{ROLES[t.role]}</span></td>
                  <td>{date(t.invite_le, true)}</td>
                  <td className="num">
                    <button type="button" className="btn btn--secondary" onClick={() => setOuverte(t)}>Ouvrir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
